/**
 * AI 분석 모델 응답 관련 에러 분류 및 사용자 친화적 메시지 유틸리티
 */

export type AIErrorCode =
    | 'NO_RESPONSE'       // 서버 응답 없음 (HTTP 오류 또는 빈 본문)
    | 'SERVER_ERROR'      // 서버 내부 오류 (500 등)
    | 'TIMEOUT'           // 요청 시간 초과
    | 'NETWORK_ERROR'     // 네트워크 연결 오류
    | 'RATE_LIMIT'        // API 사용량 한도 초과 (API 키, 429 등)
    | 'TIER_LIMIT_EXCEEDED' // 멤버십 월간 한도 초과 (403, 횟수 초과)
    | 'EMPTY_STREAM'      // 스트리밍 응답이 비어 있음
    | 'UNKNOWN';          // 기타 알 수 없는 오류

export interface AIErrorInfo {
    code: AIErrorCode;
    title: string;        // 짧은 제목 (예: "AI 모델 응답 없음")
    message: string;      // 사용자에게 표시할 상세 메시지
    suggestion: string;   // 권장 조치
    severity: 'error' | 'warning' | 'info';
    requiredTier?: string; // 업그레이드가 필요한 등급 (예: TIER3)
    featureName?: string;  // 제한된 기능명
}

const ERROR_MESSAGES_KO: Record<AIErrorCode, Omit<AIErrorInfo, 'code'>> = {
    NO_RESPONSE: {
        title: 'AI 모델 응답 없음',
        message: 'AI 분석 서버에 연결할 수 없습니다. 서버가 일시적으로 중단되었거나 네트워크에 문제가 있을 수 있습니다.',
        suggestion: '네트워크 상태를 확인하거나 잠시 후 다시 시도해 주세요.',
        severity: 'error',
    },
    SERVER_ERROR: {
        title: 'AI 분석 서버 오류',
        message: 'AI 분석 서버에서 내부 오류가 발생했습니다.',
        suggestion: '잠시 후 다시 시도해 주세요. 문제가 지속되면 관리자에게 문의하세요.',
        severity: 'error',
    },
    TIMEOUT: {
        title: 'AI 응답 시간 초과',
        message: '현재 서버 부하가 높아 AI 분석 시간이 초과되었습니다.',
        suggestion: '잠시 후 다시 시도하거나, 다른 분석 유형으로 시도해 보세요.',
        severity: 'warning',
    },
    NETWORK_ERROR: {
        title: '네트워크 오류',
        message: '인터넷 연결이 불안정하여 AI 분석을 수행할 수 없습니다.',
        suggestion: '인터넷 연결을 확인하고 방화벽 설정을 점검해 주세요.',
        severity: 'error',
    },
    RATE_LIMIT: {
        title: 'AI 분석 API 요청 한도 도달',
        message: '금일 AI 분석 API 사용량을 초과했습니다.',
        suggestion: '잠시 후 다시 시도하거나 [설정 > API 키]에서 개인 키를 등록하세요.',
        severity: 'warning',
    },
    TIER_LIMIT_EXCEEDED: {
        title: '멤버십 월간 분석 한도 초과',
        message: '이번 달 해당 AI 기능의 무료/등급 한도를 모두 사용하셨습니다.',
        suggestion: '멤버십 등급을 업그레이드하시면 더 많은 월간 한도 또는 무제한으로 이용하실 수 있습니다.',
        severity: 'warning',
    },
    EMPTY_STREAM: {
        title: '분석 결과 없음',
        message: 'AI 모델이 분석 결과를 생성하지 못했습니다. 입력 데이터에 문제가 있거나 일시적인 오류일 수 있습니다.',
        suggestion: '다시 시도하거나 다른 분석 유형으로 시도해 보세요.',
        severity: 'warning',
    },
    UNKNOWN: {
        title: 'AI 분석 오류',
        message: '알 수 없는 오류가 발생했습니다.',
        suggestion: '잠시 후 다시 시도해 주세요. 문제가 지속되면 관리자에게 문의하세요.',
        severity: 'error',
    },
};

const ERROR_MESSAGES_EN: Record<AIErrorCode, Omit<AIErrorInfo, 'code'>> = {
    NO_RESPONSE: {
        title: 'No AI Response',
        message: 'Unable to connect to the AI analysis server. The server may be temporarily down or there may be a network issue.',
        suggestion: 'Please check your network connection and try again later.',
        severity: 'error',
    },
    SERVER_ERROR: {
        title: 'AI Server Error',
        message: 'An internal error occurred on the AI analysis server.',
        suggestion: 'Please try again later. If the problem persists, contact support.',
        severity: 'error',
    },
    TIMEOUT: {
        title: 'AI Response Timeout',
        message: 'The AI analysis timed out due to high server load.',
        suggestion: 'Please try again later or try a different analysis type.',
        severity: 'warning',
    },
    NETWORK_ERROR: {
        title: 'Network Error',
        message: 'Unable to perform AI analysis due to unstable internet connection.',
        suggestion: 'Please check your internet connection and firewall settings.',
        severity: 'error',
    },
    RATE_LIMIT: {
        title: 'AI Analysis API Limit Reached',
        message: 'You have reached your daily AI analysis API quota.',
        suggestion: 'Please try again later or add your personal API key in Settings.',
        severity: 'warning',
    },
    TIER_LIMIT_EXCEEDED: {
        title: 'Membership Monthly Limit Exceeded',
        message: 'You have reached your monthly limit for this AI feature.',
        suggestion: 'Upgrade your membership tier to enjoy higher or unlimited monthly usage.',
        severity: 'warning',
    },
    EMPTY_STREAM: {
        title: 'No Analysis Result',
        message: 'The AI model did not generate any analysis result. This may be due to input data issues or a temporary error.',
        suggestion: 'Please try again or try a different analysis type.',
        severity: 'warning',
    },
    UNKNOWN: {
        title: 'AI Analysis Error',
        message: 'An unknown error occurred.',
        suggestion: 'Please try again later. If the problem persists, contact support.',
        severity: 'error',
    },
};

/**
 * 기술적 에러(Error 객체)를 상황별 AIErrorCode로 분류합니다.
 */
export function classifyAIError(error: unknown, lang: string = 'ko'): AIErrorInfo {
    const code = classifyErrorCode(error);
    const messages = lang === 'ko' ? ERROR_MESSAGES_KO : ERROR_MESSAGES_EN;
    const baseInfo = messages[code];
    let message = baseInfo.message;
    let requiredTier: string | undefined = undefined;

    if (error && typeof error === 'object') {
        const errObj = error as any;
        if (errObj.requiredTier) {
            requiredTier = errObj.requiredTier;
        }
        if (errObj.message && typeof errObj.message === 'string') {
            const customMsg = errObj.message;
            if (!customMsg.startsWith('{') && !customMsg.startsWith('HTTP ') && customMsg !== 'tier_limit_exceeded') {
                message = customMsg;
            }
        }
    } else if (typeof error === 'string' && error.length > 0 && !error.startsWith('{') && !error.startsWith('HTTP ') && error !== 'tier_limit_exceeded') {
        message = error;
    }

    return {
        code,
        title: baseInfo.title,
        message,
        suggestion: baseInfo.suggestion,
        severity: baseInfo.severity,
        requiredTier,
    };
}

/**
 * Error 객체를 AIErrorCode로 분류
 */
function classifyErrorCode(error: unknown): AIErrorCode {
    if (!error) return 'UNKNOWN';

    const errMsg = typeof error === 'string' ? error : (error as any)?.message || '';
    const errStr = typeof error === 'string' ? error : JSON.stringify(error);
    const errName = (error as any)?.name || '';

    // AbortError는 사용자 취소이므로 별도 처리 대상 아님
    if (errName === 'AbortError') return 'UNKNOWN';

    // 1. 멤버십 등급 한도 초과 (Tier Limit Exceeded)
    if (
        errMsg.includes('tier_limit') ||
        errMsg.includes('tier_limit_exceeded') ||
        errMsg.includes('TIER_LIMIT_EXCEEDED') ||
        errMsg.includes('한도(') ||
        errMsg.includes('한도 초과') ||
        errMsg.includes('한도를 초과했습니다') ||
        (errMsg.includes('Forbidden') && (errMsg.includes('한도') || errMsg.includes('등급'))) ||
        (errMsg.includes('403') && (errMsg.includes('한도') || errMsg.includes('등급'))) ||
        (errMsg.includes('한도') && errMsg.includes('업그레이드'))
    ) {
        return 'TIER_LIMIT_EXCEEDED';
    }

    // 2. 타임아웃
    if (
        errName === 'TimeoutError' ||
        errMsg.includes('timeout') ||
        errMsg.includes('Timeout') ||
        errMsg.includes('timed out') ||
        errMsg.includes('AbortError') // AbortSignal.timeout에 의한 AbortError
    ) {
        return 'TIMEOUT';
    }

    // 3. 네트워크 오류
    if (
        errMsg.includes('NetworkError') ||
        errMsg.includes('network') ||
        errMsg.includes('Network') ||
        errMsg.includes('fetch') ||
        errMsg.includes('Failed to fetch') ||
        errMsg.includes('TypeError') ||
        errMsg.includes('ERR_CONNECTION') ||
        errMsg.includes('ERR_NAME_NOT_RESOLVED') ||
        errMsg.includes('No response body') ||
        errMsg.includes('Streaming not supported') ||
        errMsg.includes('Failed to create stream reader')
    ) {
        return 'NETWORK_ERROR';
    }

    // 4. API Rate Limit / Quota Exceeded (Gemini 429)
    if (
        errMsg.includes('429') ||
        errMsg.includes('RESOURCE_EXHAUSTED') ||
        errStr.includes('429') ||
        errStr.includes('RESOURCE_EXHAUSTED') ||
        errMsg.includes('rate limit') ||
        errMsg.includes('RateLimit')
    ) {
        return 'RATE_LIMIT';
    }

    // 5. 서버 에러 (HTTP 5xx)
    if (
        errMsg.includes('500') ||
        errMsg.includes('502') ||
        errMsg.includes('503') ||
        errMsg.includes('504') ||
        errMsg.includes('Internal server error') ||
        errMsg.includes('Server Error')
    ) {
        return 'SERVER_ERROR';
    }

    // 6. 빈 응답
    if (
        errMsg.includes('empty') ||
        errMsg.includes('Empty') ||
        errMsg.includes('no content') ||
        errMsg.includes('No content') ||
        errMsg.includes('no result') ||
        errMsg.includes('No result') ||
        errMsg.includes('No response')
    ) {
        return 'EMPTY_STREAM';
    }

    // 7. HTTP 에러 코드 기반 분류
    const httpMatch = errMsg.match(/HTTP\s+(\d{3})/);
    if (httpMatch) {
        const status = parseInt(httpMatch[1], 10);
        if (status === 403) return 'TIER_LIMIT_EXCEEDED';
        if (status === 429) return 'RATE_LIMIT';
        if (status >= 500) return 'SERVER_ERROR';
        if (status >= 400) return 'NO_RESPONSE';
    }

    return 'UNKNOWN';
}

/**
 * AI 스트리밍 응답 텍스트가 유효한지 검증합니다.
 */
export function validateAIResponse(text: string): { valid: boolean; reason?: string } {
    if (!text || text.trim().length === 0) {
        return { valid: false, reason: 'empty' };
    }
    if (text.trim().length < 10) {
        return { valid: false, reason: 'too_short' };
    }
    if (/I('m| am) sorry.*can('t|not) fulfill/i.test(text) || /unable to fulfill this request/i.test(text)) {
        return { valid: false, reason: 'refusal' };
    }
    return { valid: true };
}

/**
 * AIError 클래스 — 에러 코드를 포함한 커스텀 에러
 */
export class AIError extends Error {
    public code: AIErrorCode;
    public lang: string;
    public requiredTier?: string;

    constructor(code: AIErrorCode, lang: string = 'ko', customMessage?: string, requiredTier?: string) {
        const messages = lang === 'ko' ? ERROR_MESSAGES_KO : ERROR_MESSAGES_EN;
        const info = messages[code] || messages.UNKNOWN;
        super(customMessage || info.message);
        this.name = 'AIError';
        this.code = code;
        this.lang = lang;
        this.requiredTier = requiredTier;
    }

    get userInfo(): AIErrorInfo {
        const info = classifyAIError(this, this.lang);
        if (this.requiredTier) {
            info.requiredTier = this.requiredTier;
        }
        return info;
    }
}

